import user from '../api/user';
import { atom, selectorFamily, selector } from 'recoil';
import { recoilPersist } from 'recoil-persist';

// Clear rep-related localStorage only when the URL has a guid param. Otherwise leave id/repId
// and returning-user behavior unchanged.
if (typeof window !== 'undefined') {
  const guid = new URLSearchParams(window.location.search).get('guid');
  const id = new URLSearchParams(window.location.search).get('id');
  if ((guid != null && String(guid).trim() !== '') || (id != null && String(id).trim() !== '')) {
    const REP_STORAGE_KEYS = [
      'NUL_REP',
      'NUL_GUID',
      'NUL_MARKET',
      'NUL_UPLINE_REP_DID',
      'NUL_REP_POSITION',
      'NUL_REPDID',
    ];
    REP_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  }
}

const { persistAtom: persistGuid } = recoilPersist({ key: 'NUL_GUID' });
export const guidState = atom({
  key: 'guidState',
  default: '',
  effects: [persistGuid],
});

const { persistAtom: persistRepIdParam } = recoilPersist({ key: 'NUL_GUID' });
export const repIdParamState = atom({
  key: 'repIdParamState',
  default: '',
  effects: [persistRepIdParam],
});

const { persistAtom: persistIdParam } = recoilPersist({ key: 'NUL_GUID' });
export const idParamState = atom({
  key: 'idParamState',
  default: '',
  effects: [persistIdParam],
});

const { persistAtom: persistMarketParam } = recoilPersist({ key: 'NUL_MARKET' });
export const marketParamState = atom({
  key: 'marketParamState',
  default: '',
  effects: [persistMarketParam],
});

const { persistAtom: persistUplineDID } = recoilPersist({
  key: 'NUL_UPLINE_REP_DID',
});
export const uplineRepDIDState = atom({
  key: 'uplineRepDIDState',
  default: '',
  effects: [persistUplineDID],
});

const { persistAtom: persistRepPosition } = recoilPersist({
  key: 'NUL_REP_POSITION',
});
export const repPositionState = atom({
  key: 'repPositionState',
  default: '',
  effects: [persistRepPosition],
});

const { persistAtom: persistRepDID } = recoilPersist({ key: 'NUL_REPDID' });
export const repDIDState = atom({
  key: 'repDID',
  default: '',
  effects: [persistRepDID],
});

const { persistAtom: persistRef } = recoilPersist({ key: 'NUL_REF' });
export const refState = atom({
  key: 'refState',
  default: '',
  effects: [persistRef],
});

export const persistRepState = atom({
  key: 'PersistRep',
  default: false,
});

export const repState = selectorFamily({
  key: 'RepState',
  get:
    (repDID) =>
    ({ get }) => {
      // Normalize repDID - use provided value or get from state
      const normalizedRepDID = repDID || get(repDIDState);
      
      // Check localStorage first to avoid unnecessary API calls
      const persist = get(persistRepState);
      if (persist) {
        try {
          const persistedRepStr = localStorage.getItem('NUL_REP');
          if (persistedRepStr) {
            const persistedRep = JSON.parse(persistedRepStr);
            // If we have cached rep and it matches the requested repDID (or no specific repDID requested), use it
            if (persistedRep && (!normalizedRepDID || persistedRep.RepDID === normalizedRepDID)) {
              return persistedRep;
            }
          }
        } catch (e) {
          // If localStorage parse fails, continue to API call
        }
      }
      
      // Only make API call if we don't have cached data
      return get(repQuery(normalizedRepDID));
    },
});

const repQuery = selectorFamily({
  key: 'repQuery',
  get:
    (repDID) =>
    async ({ get }) => {
      // Check localStorage again here as a final check before API call
      // (in case it was set between repState check and this execution)
      try {
        const cachedRepStr = localStorage.getItem('NUL_REP');
        if (cachedRepStr) {
          const cachedRep = JSON.parse(cachedRepStr);
          if (cachedRep && (!repDID || cachedRep.RepDID === repDID)) {
            return cachedRep;
          }
        }
      } catch (e) {
        // Continue to API call if cache check fails
      }

      let response = {};
      const guid = get(guidState);
      if (!repDID && guid) response = await user.getByGuid(get(guidState));
      if (response.error) throw response.error;
      if (response.RepDID || repDID) {
        const response2 = await user.get(response.RepDID || repDID);
        if (response2.error) throw response2.error;
        const response3 = await user.getLanguageById(response2.DefaultLocaleID || 1);
        if (response3.error) throw response3.error;
        const rep = { ...response, ...response2, ...response3 };
        localStorage.setItem('NUL_REP', JSON.stringify(rep));
        return rep;
      } else {
        return {};
      }
    },
});

// get: repDID => async ({ get }) => {
//   let response = {}
//   const guid = get(guidState)
//   if (!repDID && guid) response = await user.getByGuid(get(guidState))
//   if (response.error) throw response.error
//   if (response === 'Invalid guid') {
//     toast.error(response)
//     throw new Error(response)
//   }
//   if (response.RepDID || repDID) {
//     const response2 = await user.get(response.RepDID || repDID)
//     if (typeof response2 === 'string' && response2.includes('Cancelled'))  {
//       toast.error(response2)
//       // Don't block, just notify with toast
//       // throw response2
//     }
//     const response3 = await user.getLanguageById(response2.DefaultLocaleID || 1)
//     if (response3.error) throw response3.error
//     const rep = {...response, ...response2, ...response3};
//     localStorage.setItem('NUL_REP', JSON.stringify(rep))
//     return rep;
//   } else {
//     return {}
//   }
// },
// });

export const accountAddress = atom({
  key: 'accountAddress',
  default: {
    addressType: 'account',
    current: undefined,
    selected: 'current',
    serviceobjects: undefined,
  },
});

export const shippingAddress = atom({
  key: 'shippingAddress',
  default: {
    addressType: 'shipping',
    current: undefined,
    selected: 'current',
    serviceobjects: undefined,
  },
});

const { persistAtom: persistInputs } = recoilPersist({ key: 'NUL_INPUTS' });
export const inputState = atom({
  key: 'inputState',
  default: {
    shippingAddressSameAsAccount: true,
  },
  effects: [persistInputs],
});

/**
 * Stores last validated value and result for fields with requiresAsyncValidation.
 * Keyed by field id. Persisted to maintain validation state on page refresh.
 * Use fieldAsyncValidationForField(id) for reading/writing a single field.
 */
const { persistAtom: persistAsyncValidation } = recoilPersist({ key: 'NUL_ASYNC_VALIDATION' });
export const fieldAsyncValidationState = atom({
  key: 'fieldAsyncValidationState',
  default: {},
  effects: [persistAsyncValidation],
});

export const fieldAsyncValidationForField = selectorFamily({
  key: 'fieldAsyncValidationForField',
  get:
    (fieldId) =>
    ({ get }) =>
      get(fieldAsyncValidationState)[fieldId] || {},
  set:
    (fieldId) =>
    ({ set }, newVal) =>
      set(fieldAsyncValidationState, (prev) => ({
        ...prev,
        [fieldId]: { ...(prev[fieldId] || {}), ...newVal },
      })),
});

export const addressState = selector({
  key: 'addressState',
  default: [],
  get: ({ get }) => {
    const address1 = get(accountAddress);
    const address2 = get(shippingAddress);
    return [address1, address2];
  },
  set: ({ set }, newValue) => {
    const [val1, val2] = newValue;
    set(accountAddress, val1);
    set(shippingAddress, val2);
  },
});
